import React from 'react';
import { ExternalLink, XCircle } from 'lucide-react';
import type { Theme } from '../../types';
import { resolveThemeToken } from '../../utils/theme';
import { isValidImageUrl } from '../../utils/fileValidation';
import ExportMenu from './ExportMenu';
import ViewTabs from '../ViewTabs';
import type { WidgetStrings } from '../../utils/i18n';
import { DEFAULT_WIDGET_STRINGS, formatWidgetString } from '../../utils/i18n';

interface HeaderProps {
  view: string;
  viewCounts: Record<string, number>;
  theme: Theme;
  companyName: string;
  companyLogo: string;
  agentName: string;
  agentAvatar: string;
  titleId?: string;
  tabsIdBase: string;
  showViewTabs: boolean;
  showSessionsButton?: boolean;
  onSessionsClick?: () => void;
  onViewChange: (view: string) => void;
  onMinimize: () => void;
  onExport: (format: string) => void;
  settingsConsoleUrl?: string;
  settingsConsoleLabel?: string;
  strings?: WidgetStrings;
}

const Header = React.memo(function Header({
  view,
  viewCounts,
  companyName,
  companyLogo,
  agentName,
  agentAvatar,
  theme,
  titleId,
  tabsIdBase,
  showViewTabs,
  showSessionsButton,
  onSessionsClick,
  onViewChange,
  onMinimize,
  onExport,
  settingsConsoleUrl,
  settingsConsoleLabel,
  strings = DEFAULT_WIDGET_STRINGS,
}: HeaderProps) {
  const headerBackground = resolveThemeToken(theme?.header || 'bg-white', 'backgroundColor');
  const settingsLinkLabel = settingsConsoleLabel || strings.settingsLink;
  return (
    <div
      className={`rw-header border-b ${headerBackground.className}`}
      style={headerBackground.style}
      role="banner"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {agentAvatar && isValidImageUrl(agentAvatar) && (
            <img
              src={agentAvatar}
              alt={`${agentName} avatar`}
              className="w-8 h-8 rounded-full border border-gray-200 object-cover"
              loading="lazy"
              decoding="async"
              onError={e => {
                (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
          )}
          <div className="leading-tight text-gray-900">
            <h2 className="text-sm font-semibold" id={titleId}>
              {companyLogo && isValidImageUrl(companyLogo) ? (
                <>
                  <img
                    src={companyLogo}
                    alt={`${companyName} logo`}
                    className="h-3 w-auto max-w-[90px] object-contain"
                    loading="lazy"
                    decoding="async"
                    onError={e => {
                      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                    }}
                  />
                  <span className="sr-only">{agentName}</span>
                </>
              ) : (
                agentName
              )}
            </h2>
            <div className="text-xs opacity-90">
              {formatWidgetString(strings.onlineWithCompany, { company: companyName })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showSessionsButton && (
            <button
              onClick={onSessionsClick}
              type="button"
              className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
              title={strings.sessions}
              aria-label={strings.sessions}
            >
              <svg
                className="w-4 h-4 text-gray-900"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M3 12a9 9 0 1 0 9-9" />
                <path d="M3 4v4h4" />
                <path d="M12 7v5l4 2" />
              </svg>
            </button>
          )}
          {settingsConsoleUrl && (
            <a
              href={settingsConsoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
              title={settingsLinkLabel}
              aria-label={settingsLinkLabel}
            >
              <ExternalLink className="w-3.5 h-3.5 text-gray-900" aria-hidden="true" />
              <span className="text-xs">{settingsLinkLabel}</span>
            </a>
          )}
          <ExportMenu onExport={onExport} strings={strings} />
          <button
            onClick={onMinimize}
            type="button"
            className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
            title={strings.minimizeChat}
            aria-label={strings.minimizeChat}
          >
            <XCircle className="w-4 h-4 text-gray-900" aria-hidden="true" />
          </button>
        </div>
      </div>
      {showViewTabs ? (
        <ViewTabs view={view} viewCounts={viewCounts} onChange={onViewChange} idBase={tabsIdBase} />
      ) : null}
    </div>
  );
});

export default Header;
